import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { riskApi } from '../../utils/riskApi';
import { formatStamp } from '../../utils/datetime';
import TakeBaselineModal from './TakeBaselineModal';
import { health, HealthDelta, Tip } from './riskUi';
import '../../styles/DocReview.css';
import '../../styles/Risk.css';

const BUCKETS = [
  ['added', 'Added', { background: '#DDF3E4', color: '#17603A' }],
  ['removed', 'Removed', { background: '#F3F0F8', color: '#4A4560' }],
  ['score_up', 'Riskier', { background: '#FBE0D5', color: '#8A2A0B' }],
  ['score_down', 'Less risky', { background: '#E3ECFB', color: '#1F4E9E' }],
  ['control_changed', 'Control changed', { background: '#FFF1D6', color: '#7A4B00' }],
  ['text_changed', 'Text changed', { background: '#EAE5F5', color: '#3D2070' }],
];
const BUCKET_STYLE = Object.fromEntries(BUCKETS.map(([k, , st]) => [k, st]));
const BUCKET_LABEL = Object.fromEntries(BUCKETS.map(([k, l]) => [k, l]));

/**
 * Baselines & compare (mockup screen 6). Left: the panel's baselines. Right: compare two
 * points (a baseline, or Now), the change by bucket, and the AI analysis of that change.
 */
export default function BaselinesPage({ aiEnabled, canManagePanels }) {
  const { setupId } = useParams();
  const navigate = useNavigate();
  const [panel, setPanel] = useState(null);
  const [list, setList] = useState(null);
  const [a, setA] = useState('');
  const [b, setB] = useState('now');
  const [cmp, setCmp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bucket, setBucket] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [analysing, setAnalysing] = useState(false);
  const [taking, setTaking] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [cfg, bl] = await Promise.all([riskApi.panelConfig(setupId), riskApi.baselines(setupId)]);
      setPanel(cfg);
      setList(bl.baselines || []);
      const live = (bl.baselines || []).filter((x) => !x.archived_at);
      setA((cur) => cur || (live[0]?.id || ''));
    } catch (e) {
      setError(e.message);
      setList((x) => x || []);
    }
  }, [setupId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!a) { setCmp(null); return undefined; }
    let live = true;
    setLoading(true);
    setBucket('');
    riskApi.compare(setupId, a, b)
      .then((d) => { if (live) { setCmp(d); setAnalysis(d.analysis || null); setError(''); } })
      .catch((e) => { if (live) setError(e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [setupId, a, b]);

  const analyse = async (force = false) => {
    setAnalysing(true);
    try {
      const r = await riskApi.analyse(setupId, { a, b, force });
      setAnalysis(r.analysis);
    } catch (e) {
      setError(e.message);
    } finally {
      setAnalysing(false);
    }
  };

  const archive = async (x) => {
    try {
      await riskApi.archiveBaseline(setupId, x.id, !x.archived_at);
      load();
    } catch (e) { setError(e.message); }
  };

  const live = (list || []).filter((x) => !x.archived_at);
  const changes = (cmp?.diff?.changes || []).filter((c) => !bucket || c.bucket === bucket);
  const sa = health(cmp?.score_a?.score);
  const sb = health(cmp?.score_b?.score);

  return (
    <div className="drv-page rsk-page">
      <div className="rsk-crumbs">
        <Link to="/risk">Risk</Link><span>/</span>
        <span className="rsk-dim">{panel?.name || 'Risk file'}</span><span>/</span>
        <strong>Baselines</strong>
      </div>
      <div className="drv-head">
        <div>
          <h1 className="drv-title">Baselines</h1>
          <p className="drv-sub">Read-only snapshots of the risk file — compare any two, or a baseline with now.</p>
        </div>
        {canManagePanels && panel && (
          <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={() => setTaking(true)}>Take baseline</button>
        )}
      </div>
      {error && <div className="drv-error" role="alert">{error}</div>}

      <div className="rsk-two">
        <aside className="rsk-bl-list" aria-label="Baselines">
          <div className="rsk-lbl">Baselines · {live.length}</div>
          {list === null && <div className="drv-muted">Loading…</div>}
          {list?.length === 0 && (
            <div className="drv-empty" style={{ padding: 18 }}>
              No baselines yet. Take one before a design review or a submission — then every later change
              can be compared against it.
            </div>
          )}
          {(list || []).map((x) => (
            <div key={x.id} className={`rsk-bl${x.archived_at ? ' rsk-bl--arch' : ''}`}>
              <div className="rsk-row-between">
                <strong>{x.name}</strong>
                <span className="rsk-bl-score">{health(x.score) ?? '—'}</span>
              </div>
              <div className="rsk-fine">{formatStamp(x.created_at, { dateOnly: true })} · {x.created_by_name} · {x.n_risks} risks</div>
              <Tip as="div" className="rsk-sha" heading="SHA-256 fingerprint"
                tip={<span className="rsk-tip-mono">{x.sha256}</span>}>SHA-256 {x.sha256?.slice(0, 4)}…{x.sha256?.slice(-4)}</Tip>
              {x.note && <div className="rsk-fine">{x.note}</div>}
              {canManagePanels && (
                <button type="button" className="drv-link rsk-small-link" style={{ marginLeft: 0, alignSelf: 'flex-start' }}
                  onClick={() => archive(x)}>{x.archived_at ? 'Restore' : 'Archive'}</button>
              )}
            </div>
          ))}
          <div className="rsk-fine">
            A baseline keeps every risk, score, trace and control status, plus the FMEA settings used. It can be
            archived but never edited or deleted.
          </div>
        </aside>

        <section className="rsk-cmp" aria-label="Compare">
          {live.length > 0 && (
            <div className="rsk-card rsk-pad rsk-cmp-bar">
              <label className="drv-field">
                <span className="drv-lbl">From</span>
                <select className="drv-select" value={a} onChange={(e) => setA(e.target.value)}>
                  {live.map((x) => <option key={x.id} value={x.id}>{x.name} — {formatStamp(x.created_at, { dateOnly: true })}</option>)}
                </select>
              </label>
              <span className="rsk-arr" aria-hidden="true">→</span>
              <label className="drv-field">
                <span className="drv-lbl">To</span>
                <select className="drv-select" value={b} onChange={(e) => setB(e.target.value)}>
                  <option value="now">Now (live)</option>
                  {live.filter((x) => x.id !== a).map((x) => <option key={x.id} value={x.id}>{x.name} — {formatStamp(x.created_at, { dateOnly: true })}</option>)}
                </select>
              </label>
              {cmp && (
                <div className={cmp.same_settings ? 'rsk-ok-line' : 'drv-note'} style={{ flexBasis: '100%' }}>
                  {cmp.same_settings
                    ? '✓ Same FMEA settings on both sides — a level move is a real risk change.'
                    : '⚠ The FMEA settings differ between these two points. A level move may be a change of scale, not of risk.'}
                </div>
              )}
            </div>
          )}

          {loading && <div className="drv-muted">Comparing…</div>}

          {cmp && !loading && (
            <>
              <div className="rsk-tiles">
                <div className="rsk-tile rsk-tile--static">
                  <span className="rsk-tile-name">Product risk score</span>
                  <span className="rsk-tile-n">{sa ?? '—'} → {sb ?? '—'}
                    {sa != null && sb != null && sa !== sb && (
                      <HealthDelta from={sa} to={sb} />
                    )}
                  </span>
                  <span className="rsk-tile-hint">{cmp.diff.n_a} → {cmp.diff.n_b} risks</span>
                </div>
                <button type="button" className={`rsk-tile${!bucket ? ' rsk-tile--on' : ''}`} onClick={() => setBucket('')}>
                  <span className="rsk-tile-name">All changes</span>
                  <span className="rsk-tile-n">{cmp.diff.changes.length}</span>
                  <span className="rsk-tile-hint">{cmp.diff.unchanged} unchanged</span>
                </button>
                {BUCKETS.map(([k, label]) => (
                  <button key={k} type="button" className={`rsk-tile${bucket === k ? ' rsk-tile--on' : ''}`}
                    onClick={() => setBucket(bucket === k ? '' : k)} disabled={!cmp.diff.counts[k]}>
                    <span className="rsk-tile-name">{label}</span>
                    <span className="rsk-tile-n">{cmp.diff.counts[k]}</span>
                  </button>
                ))}
              </div>

              <div className="rsk-card rsk-pad rsk-ai">
                <div className="rsk-row-between">
                  <div className="rsk-lbl">AI analysis of this change</div>
                  {analysis && (
                    <span className="rsk-fine">
                      {analysis.created_at ? `${formatStamp(analysis.created_at)} · ` : ''}
                      {aiEnabled && <button type="button" className="drv-link rsk-small-link" onClick={() => analyse(true)} disabled={analysing}>Regenerate</button>}
                    </span>
                  )}
                </div>
                {!analysis && (
                  aiEnabled ? (
                    <button type="button" className="drv-btn" style={{ alignSelf: 'flex-start' }} onClick={() => analyse(false)} disabled={analysing}>
                      {analysing ? 'Analysing…' : 'Analyse with AI'}
                    </button>
                  ) : <div className="rsk-fine">AI is not enabled for this account.</div>
                )}
                {analysis && (
                  <>
                    <p className="rsk-ai-text">{analysis.summary}</p>
                    {analysis.worth_a_look?.length > 0 && <div className="rsk-lbl">Worth a look</div>}
                    {(analysis.worth_a_look || []).map((w) => (
                      <div className="rsk-look" key={w.text}>
                        <span className="rsk-pill" style={{ background: '#FFF1D6', color: '#7A4B00' }}>{w.kind}</span>
                        <span className="rsk-look-text">{w.text}</span>
                        <span className="rsk-look-keys">
                          {(w.keys || []).map((k) => {
                            const ch = cmp.diff.changes.find((c) => c.key === k);
                            return ch
                              ? <button type="button" key={k} className="drv-link" onClick={() => navigate(`/risk/${setupId}/r/${ch.item_id}`)}>{k}</button>
                              : <span key={k}>{k}</span>;
                          })}
                        </span>
                      </div>
                    ))}
                    <div className="rsk-fine">AI suggestions — check them against the risks before acting.</div>
                  </>
                )}
              </div>

              <div className="rsk-card">
                <div className="rsk-pad rsk-sub" style={{ borderBottom: '1px solid #EFECF6' }}>
                  <strong style={{ color: '#1A1631' }}>{bucket ? `${changes.length} ${BUCKET_LABEL[bucket].toLowerCase()}` : `${cmp.diff.changes.length} changed · ${cmp.diff.unchanged} unchanged`}</strong>
                  {' '}· matched by item id
                </div>
                <div className="rsk-table-wrap">
                  <table className="rsk-table" style={{ minWidth: 900 }}>
                    <thead><tr><th>Risk</th><th>Change</th><th>Before</th><th>After</th><th>Detail</th></tr></thead>
                    <tbody>
                      {changes.map((c) => (
                        <tr key={`${c.bucket}-${c.item_id}`} className={c.bucket === 'removed' ? '' : 'rsk-tr'}
                          onClick={c.bucket === 'removed' ? undefined : () => navigate(`/risk/${setupId}/r/${c.item_id}`)}>
                          <td><span className="rsk-key">{c.key}</span><div className="rsk-name">{c.name}</div></td>
                          <td><span className="rsk-pill" style={BUCKET_STYLE[c.bucket]}>{BUCKET_LABEL[c.bucket]}</span></td>
                          <td className="rsk-sub">{c.before}</td>
                          <td className="rsk-sub">{c.after}</td>
                          <td className="rsk-sub">
                            {(c.details || []).map((d) => <div key={d}>{d}</div>)}
                            {(c.text || []).map((t) => (
                              <div key={t.field}>{t.field}: <del>{t.before || '—'}</del> <ins>{t.after || '—'}</ins></div>
                            ))}
                          </td>
                        </tr>
                      ))}
                      {changes.length === 0 && (
                        <tr><td colSpan={5} className="rsk-dim" style={{ textAlign: 'center', padding: 24 }}>No changes.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {taking && panel && (
        <TakeBaselineModal panel={{ id: setupId, name: panel.name }} onClose={() => setTaking(false)}
          onTaken={(bl) => { setTaking(false); setA(bl.id); load(); }} />
      )}
    </div>
  );
}
